import { globalApp } from "../app";
import { PageContext } from "../page-context";
import type mdbTypes from '../ext/mdb.min.js';
import { ColumnDefinition, Tabulator, TabulatorFull } from "tabulator-tables";
import { clearLocalStorage, getLocalStorageWithExpiry, setLocalStorageWithExpiry } from "../common";
declare const mdb: typeof mdbTypes

export class BaseView extends HTMLElement {
    static icon = "bi bi-file-earmark";

    /** @type {PageContext} */
    context: PageContext;
    container: string;
    table!: TabulatorFull;

    /**
     * constructor
     * @param {PageContext} context - The context of the page.
     * @param {string} [container=".main"] - The container to render the view in.
     */
    constructor(context: PageContext, container: string = ".main") {
        super();
        this.context = context;
        this.container = container;
        this.className = "pageview";
        this.addEventListener("rendered", this.onRendered);
        this.ValidateLoginData();


    }


    async checkVersionAsync() {
        if (window.isTest) {
            return;
        }
        let response = await fetch('version.json', {
            headers: {
                'Cache-Control': 'no-cache'
            }
        })

        let data = await response.json();

        let version = getLocalStorageWithExpiry("version");
        if (!version)
            setLocalStorageWithExpiry("version", data.version, 1000 * 60 * 60 * 24 * 7);

        if (version !== data.version) {
            setLocalStorageWithExpiry("version", data.version, 1000 * 60 * 60 * 24 * 7);

            if (this.context && this.context.store) {
                this.context.store.clearStoreLocalStorage("config");
                this.context.store.clearStoreLocalStorage("skillStructure");
                this.context.store.clearStoreLocalStorage("employees");
                this.context.store.clearStoreLocalStorage("projects");
                this.context.store.clearStoreLocalStorage("assignments");
                this.context.store.clearStoreLocalStorage("absences");
                this.context.store.clearStoreLocalStorage("employeesUtilization");
            } else {
                clearLocalStorage("config");
                clearLocalStorage("skillStructure");
                clearLocalStorage("employees");
                clearLocalStorage("projects");
                clearLocalStorage("assignments");
                clearLocalStorage("absences");
                clearLocalStorage("employeesUtilization");
            }
        }

        const versionElement = document.getElementById('version');
        if (versionElement)
            versionElement.innerText = "v" + data.version;
    }


    async ValidateLoginData() {
        if (this.context.authentication)
            if (this.context.user === null || this.context.user === undefined) {
                this.context.user = await this.context.authentication.getCurrentUser();
            }
    }

    /**
     * render the view
     * @returns {BaseView}
     */
    connectedCallback() {
        document.title = "Assignments";
        this.innerHTML = `<span class="placeholder col-7"></span>`;
        setTimeout(() => { this.dispatchEvent(new Event("rendered")); }, 0);
        return this;
    }

    onRendered() {
        this.hideLoading();
    }

    showLoading() {
        $(".loader").show();
    }

    hideLoading() {
        $(".loader").hide();
    }

    columnToggleFromLocalStorage(tableColumns: ColumnDefinition[]) {
        const viewName = this.constructor.name;
        const columns = this.context.userPreferences?.getPreference(viewName + "_columns");
        if (columns) {
            for (const col of columns) {
                const tableCol = tableColumns.find(c => c.field === col.field);
                if (!tableCol) continue;
                tableCol.visible = col.visible;
            }
        }
    }

    columnToggler(toggleButton: HTMLElement, table: Tabulator) {
        if (!toggleButton) return;

        table = table || (globalApp.app.context.view ? globalApp.app.context.view.table : null);
        const viewName = globalApp.app.context.view?.constructor.name;
        const popover = new mdb.Popover(toggleButton, {
            html: true,
            sanitize: false,
            trigger: "manual",
            customClass: 'toggle-columns-popover',
            content: function () {
                const wrapper = document.createElement("div");
                wrapper.classList.add("input-group");
                const ul = document.createElement("ul");
                ul.classList.add("list-group", "list-group-flush");
                wrapper.appendChild(ul);
                for (const col of table.getColumns()) {
                    if (col.getField().startsWith("plannedWeeks.cw_")) continue;
                    const li = document.createElement("li");
                    li.classList.add("list-group-item");
                    const label = document.createElement("label");
                    const input = document.createElement("input");
                    input.type = "checkbox";
                    input.classList.add("form-check-input", "chk-toggle-cols");
                    input.checked = col.isVisible();
                    input.onclick = () => {
                        table.toggleColumn(col.getField());
                        globalApp.app.context.userPreferences.setPreference(viewName + "_columns", table.getColumns()
                            .filter(col => !col.getField().startsWith("cw_"))
                            .map(col => { return { field: col.getField(), visible: col.isVisible() }; }));
                    }
                    label.appendChild(input);
                    label.appendChild(document.createTextNode(col.getDefinition().title));
                    li.appendChild(label);
                    ul.appendChild(li);
                }
                return wrapper;
            }
        });

        toggleButton.title = "Show columns";

        // Custom behavior for the Toggle Columns popover:
        // Expected behavior would be: click to open, clicking anything inside popup keep showing popup. Clicking outside, or clicking the button again, to close.
        toggleButton.onclick = (e) => {
            popover.toggle();

            setTimeout(() => {
                const popoverElement = document.getElementsByClassName("toggle-columns-popover");
                const clickOutsideToggleColumnsPopoverElementFunction = (ev: any) => {
                    if (popoverElement && popoverElement.length) {
                        const isClickInside = popoverElement[0].contains(ev.target)
                        if (!isClickInside) {
                            popover.hide();
                            document.removeEventListener("click", clickOutsideToggleColumnsPopoverElementFunction);
                        }
                    } else document.removeEventListener("click", clickOutsideToggleColumnsPopoverElementFunction);
                }

                if (popoverElement && popoverElement.length) {
                    document.addEventListener("click", clickOutsideToggleColumnsPopoverElementFunction);
                } else document.removeEventListener("click", clickOutsideToggleColumnsPopoverElementFunction);
            }, 100)
        }
    }

    getHTML5DateValue(date: string | Date | null) {
        if (!date) {
            return "";
        }
        const d = new Date(date);
        try { //so much code for a simple date format ;-)
            return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
        } catch (e) {
            return "";
        }
    }

    getUTCWeekDay = (year: number, weekNumber: number, dayOfWeek: number) => {
        const firstDayOfYear = new Date(Date.UTC(year, 0, 1));
        const firstMonday = new Date(firstDayOfYear.setDate(firstDayOfYear.getDate() - (firstDayOfYear.getDay() || 7) + 1));
        const targetDate = new Date(firstMonday.setDate(firstMonday.getDate() + (weekNumber - 1) * 7 + (dayOfWeek - 1)));
        return targetDate;
    };

    /**
     * return result of modal
     * @returns {Promise}
     */
    async getResult(): Promise<unknown> {
        return null;
    }

}